function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import { AttrsSchema, Component, ConfigOptions, jsx } from 'panel';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { panel2react } from 'common/util/react';
import { emitTrackEvent, KEY_VALUES } from 'common/util/dom';
import { SvgIconEl } from 'common/components/svg-icon';
import { InputEl } from '../input';
import { TooltipEl } from '../tooltip';
import { BreadcrumbsState, LinkNode, LinkNodeStyle } from './types';
import { TrackingEvent } from './tracking';
import css from './index.inline.styl';
const COMPONENT_NAME = "mp-breadcrumbs";
interface BreadcrumbsJsxProps {
  attrs?: BreadcrumbsAttrs;
  props: {
    linkNodes: LinkNode[];
  };
}
export enum BreadcrumbsTheme {
  Default = "default",
  Path = "path",
}
interface BreadcrumbsAttrs {
  'track-name'?: string;
  theme?: BreadcrumbsTheme;
}
const TitleInput = (_ref: {
  value;
  onInputChanged;
  onEditStop?;
}) => {
  let {
    value,
    onInputChanged,
    onEditStop
  }: {
    value;
    onInputChanged;
    onEditStop?;
  } = _ref;
  return <InputEl attrs={{
    elref: "titleInput"
  }} props={{
    value
  }} on={{
    blur: ev => onInputChanged === null || onInputChanged === void 0 ? void 0 : onInputChanged(ev.target.value),
    keydown: (ev: any) => {
      if (ev.key === KEY_VALUES.ESCAPE) {
        ev.preventDefault();
        onEditStop === null || onEditStop === void 0 || onEditStop();
      } else if (ev.key === KEY_VALUES.ENTER) {
        ev.preventDefault();
        onInputChanged === null || onInputChanged === void 0 || onInputChanged(ev.target.value);
      }
    }
  }} hook={{
    insert: (vnode: any) => {
      requestAnimationFrame(() => {
        vnode.elm.focus();
      });
    }
  }} data-sentry-element="InputEl" data-sentry-component="TitleInput" data-sentry-source-file="index.tsx" />;
};
const BreadcrumbNode = (_ref2: {
  node: LinkNode;
  idx: number;
  isEditing: boolean;
  totalNodes: number;
  onNodeClicked: (node: LinkNode, idx: number) => void;
  onValueChanged?: (value: string) => void;
}) => {
  var _node$tooltip;
  let {
    node,
    idx,
    isEditing,
    totalNodes,
    onNodeClicked,
    onValueChanged
  }: {
    node: LinkNode;
    idx: number;
    isEditing: boolean;
    totalNodes: number;
    onNodeClicked: (node: LinkNode, idx: number) => void;
    onValueChanged?: (value: string) => void;
  } = _ref2;
  const isLastNode = idx === totalNodes - 1;
  if (isEditing) {
    return <div sel='.breadcrumb-node' attrs={{
      elref: isLastNode ? "breadcrumbLastNode" : "breadcrumbNode"
    }} class={{
      'last-node': isLastNode,
      'has-multiple-nodes': totalNodes > 1,
      'editing-mode': true
    }}>
        <TitleInput value={node.label} onInputChanged={value => onValueChanged === null || onValueChanged === void 0 ? void 0 : onValueChanged(value)} />
      </div>;
  }
  return <div sel='.breadcrumb-node' class={{
    'last-node': isLastNode,
    'has-multiple-nodes': totalNodes > 1,
    disabled: node.disabled
  }} data-sentry-component="BreadcrumbNode" data-sentry-source-file="index.tsx">
      {!node.style || node.style === LinkNodeStyle.Link ? <div sel='.step.link' on={{
      click: () => onNodeClicked === null || onNodeClicked === void 0 ? void 0 : onNodeClicked(node, idx)
    }} attrs={isLastNode && {
      elref: "breadcrumbLastNode"
    }}>
          {node.url ? <a attrs={{
        elref: isLastNode ? "breadcrumbLastNode" : "breadcrumbNode",
        href: node.url
      }}>{node.label}</a> : node.label}
        </div> : <div sel='.step' class={{
      selected: node.style === LinkNodeStyle.Selected,
      secondary: node.style === LinkNodeStyle.Secondary
    }}>
          {node.label}
        </div>}
      <TooltipEl attrs={(_node$tooltip = node.tooltip) !== null && _node$tooltip !== void 0 ? _node$tooltip : {
      text: node.label,
      'max-width': "250px",
      'break-word': true
    }} data-sentry-element="TooltipEl" data-sentry-source-file="index.tsx" />
    </div>;
};
const SeparatorIcon = (_ref3: {
  theme: BreadcrumbsTheme;
}) => {
  let {
    theme
  }: {
    theme: BreadcrumbsTheme;
  } = _ref3;
  return theme === BreadcrumbsTheme.Path ? <div sel='.slash'>/</div> : <SvgIconEl attrs={{
    icon: "caret-right"
  }} />;
};
export class Breadcrumbs extends Component<BreadcrumbsState, BreadcrumbsAttrs> {
  static get attrsSchema(): AttrsSchema<BreadcrumbsAttrs> {
    return _objectSpread({
      'track-name': {
        type: "string",
        description: "If specified, fire a track event and track call"
      },
      theme: {
        type: "string",
        tsType: "BreadcrumbsTheme",
        default: BreadcrumbsTheme.Default
      }
    }, super.attrsSchema);
  }
  get config(): ConfigOptions<BreadcrumbsState> {
    return {
      css,
      template: () => {
        return <div sel='.breadcrumbs' class={{
          ["".concat(this.attr("theme"), "-theme")]: true
        }}>
            {this.state.linkNodes.map((linkedNode, idx) => <>
                <BreadcrumbNode node={linkedNode} idx={idx} isEditing={linkedNode.isInEditMode} totalNodes={this.state.linkNodes.length} onNodeClicked={(node, index) => {
              this.handleNodeClicked(node, index);
            }} onValueChanged={newLabel => {
              this.dispatchEvent(new CustomEvent("nodeUpdated", {
                detail: {
                  node: _objectSpread(_objectSpread({}, linkedNode), {}, {
                    label: newLabel
                  })
                }
              }));
            }} />

                {idx < this.state.linkNodes.length - 1 ? <SeparatorIcon theme={this.attr("theme")} /> : null}
              </>)}
          </div>;
      },
      useShadowDom: true,
      defaultState: {
        linkNodes: []
      }
    };
  }
  handleNodeClicked(node, index) {
    this.dispatchEvent(new CustomEvent("nodeClick", {
      detail: {
        node,
        index
      }
    }));
    const props = _objectSpread(_objectSpread({}, node), {}, {
      index,
      eventName: this.attr("track-name")
    });
    if (this.attr("track-name")) {
      emitTrackEvent(this, {
        eventName: TrackingEvent.ClickedLink,
        eventPrefix: COMPONENT_NAME,
        props
      });
    }
  }
  get linkNodes() {
    return this.state.linkNodes;
  }
  set linkNodes(linkNodes: LinkNode[]) {
    this.update({
      linkNodes
    });
  }
}
export const BreadcrumbsEl = defineAndMakeJsxFactory<BreadcrumbsJsxProps>(COMPONENT_NAME, Breadcrumbs);
export const BreadcrumbsReact = panel2react<BreadcrumbsJsxProps>(COMPONENT_NAME, Breadcrumbs);